// javascript
import React, { useState, useEffect } from 'react';
import Link from "./LinkHelper.jsx";
import { l, getLang } from '../../lang';
import { supabase } from '../../utils/supabase.js';

const MainMenu = React.memo(({ navigate, setLang, lang }) => {

    const [menuMap, setMenuMap] = useState(null);

    useEffect(() => {
        supabase
            .from('menu_content')
            .select('key, fr, en')
            .then(({ data }) => {
                if (data) {
                    const map = {};
                    data.forEach(row => { map[row.key] = { fr: row.fr, en: row.en }; });
                    setMenuMap(map);
                }
            });
    }, []);

    const t = (key) => menuMap?.[key]?.[lang] || l(key, lang);

    const [activeClasses, setActiveClasses] = useState({
        classHomeActive: '',
        classProjectActive: '',
        classAboutActive: '',
        classContactActive: ''
    });

    const changeLang = (newLang) => {
        try {
            if (typeof window !== 'undefined') {
                localStorage.setItem('lang', newLang);
            }
        } catch (e) { /* ignore */ }
        if (typeof setLang === 'function') setLang(newLang);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('langchange', { detail: newLang }));
        }
    };
    useEffect(() => {
        if (typeof document !== 'undefined') document.documentElement.lang = lang;
        const onStorage = (e) => {
            if (e.key === 'lang' && e.newValue && e.newValue !== lang) {
                setLang(e.newValue);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [lang, setLang]);

    const getPath = () => {
        const p = (window.location && window.location.pathname) || '/';
        return p.replace(/\/+$/, '') || '/';
    };

    const updateActiveClasses = () => {
        const path = getPath();
        setActiveClasses({
            classHomeActive: path === '/' ? 'active' : '',
            classProjectActive: path === '/projects' || path.startsWith('/projects/') ? 'active' : '',
            classAboutActive: path === '/about' || path.startsWith('/about/') ? 'active' : '',
            classContactActive: path === '/contact' || path.startsWith('/contact/') ? 'active' : ''
        });
    };

    useEffect(() => {
        updateActiveClasses();

        const onLocationChange = () => updateActiveClasses();
        const onPop = () => onLocationChange();

        window.addEventListener('popstate', onPop);

        const originalPush = history.pushState;
        const originalReplace = history.replaceState;

        history.pushState = function (...args) {
            originalPush.apply(this, args);
            window.dispatchEvent(new Event('locationchange'));
        };

        history.replaceState = function (...args) {
            originalReplace.apply(this, args);
            window.dispatchEvent(new Event('locationchange'));
        };

        window.addEventListener('locationchange', onLocationChange);

        return () => {
            window.removeEventListener('popstate', onPop);
            window.removeEventListener('locationchange', onLocationChange);
            history.pushState = originalPush;
            history.replaceState = originalReplace;
        };
    }, []);

    useEffect(() => {

        if (typeof document === 'undefined') return;
        const els = Array.from(document.querySelectorAll('.nav'));
        const handlers = els.map((el) => {
            const menu = el.closest('.main-menu')?.querySelector('.menu');
            const handler = () => {
                el.classList.toggle('open');
                if (menu) menu.classList.toggle('open');
            };
            el.addEventListener('click', handler);
            return { el, handler };
        });
        return () => {
            handlers.forEach(({ el, handler }) => el.removeEventListener('click', handler));
        };
    }, []);

    return (
        <header className='main-menu'>
            <nav className="nav">
                <ul className="menu-list">
                    <li>
                        <Link navigate={navigate} className={`${activeClasses.classHomeActive}`}
                              href="/"
                              aria-label="Go to home page"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 48 48" fill="#000000"><g fill="none" stroke="currentColor" strokeWidth={4}><path strokeLinecap="round" strokeLinejoin="round" d="M9 18v24h30V18L24 6L9 18Z"/><path strokeLinejoin="round" d="M19 29v13h10V29H19Z"/><path strokeLinecap="round" d="M9 42h30"/></g></svg>
                            <span className="text-link-inner">{t('home')}</span>
                        </Link>
                    </li>
                    <li>
                        <Link navigate={navigate} className={`${activeClasses.classAboutActive}`}
                              href="/about"
                              aria-label="Go to about page"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path fill="currentColor" fillRule="evenodd" d="M256 42.667C138.18 42.667 42.667 138.179 42.667 256c0 117.82 95.513 213.334 213.333 213.334c117.822 0 213.334-95.513 213.334-213.334S373.822 42.667 256 42.667m0 384c-94.105 0-170.666-76.561-170.666-170.667S161.894 85.334 256 85.334c94.107 0 170.667 76.56 170.667 170.666S350.107 426.667 256 426.667m26.714-256c0 15.468-11.262 26.667-26.497 26.667c-15.851 0-26.837-11.2-26.837-26.963c0-15.15 11.283-26.37 26.837-26.37c15.235 0 26.497 11.22 26.497 26.666m-48 64h42.666v128h-42.666z"/></svg>
                            <span className="text-link-inner">{t('about')}</span>
                        </Link>
                    </li>
                    <li>
                        <Link navigate={navigate} className={`${activeClasses.classProjectActive}`}
                              href="/projects"
                              aria-label="Go to projects page"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path fill="currentColor" fillRule="evenodd" d="m85.333 160.617l128-74l.043.025L256 62l-42.667-24.666L42.667 136v197.333L85.333 358zm87.581 23.701l104.419-60.367l104.43 60.373l-104.419 60.368zm-23.581 35.651V346.05L256 407.716v-126.08zm256 126.081l-106.667 61.666V281.649l106.667-61.667zm-128-271.383L448 173.333v197.334l-170.667 98.667l-170.666-98.667V173.333z" clipRule="evenodd"/></svg>
                            <span className="text-link-inner">{t('projects')}</span>
                        </Link>
                    </li>
                    <li>
                        <Link navigate={navigate} className={`${activeClasses.classContactActive}`}
                              href="/contact"
                              aria-label="Go to contact page"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 24 24"><path fill="currentColor" d="M2 3H0v18h24V3H2zm20 2v14H2V5h20zM10 7H6v4h4V7zm-6 6h8v4H4v-4zm16-6h-6v2h6V7zm-6 4h6v2h-6v-2zm6 4h-6v2h6v-2z"/></svg>
                            <span className="text-link-inner">{t('contact')}</span>
                        </Link>
                    </li>
                    <li className="lang-switch">
                        <button
                            type="button"
                            className={`lang-btn cursor-event ${lang === 'fr' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); changeLang('fr'); }}
                            aria-pressed={lang === 'fr'}
                            aria-label="French language"
                        >
                            <span>FR</span>
                        </button>
                        <button
                            type="button"
                            className={`lang-btn cursor-event ${lang === 'en' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); changeLang('en'); }}
                            aria-pressed={lang === 'en'}
                            aria-label="English language"
                        >
                            <span>EN</span>
                        </button>
                    </li>
                </ul>
            </nav>
        </header>
    );
});

export default MainMenu;